import { interactiveHasContent } from "./interactiveAnswerValue";

/**
 * Is this stored answer value empty?
 *
 * One canonical test for every take page. The mock-exam page had a second, narrower copy inline in
 * `handleAnswer` that only understood `{ text, drawing }`, so placing points on an interactive
 * canvas, declaring "no lines of symmetry", a cut-drag answer or a matching map flipped the palette
 * square to UNANSWERED at the exact moment the student answered — while the answered-count and the
 * marks tally, which used the fuller test, disagreed.
 */
export const isAnswerValueEmpty = (val) => {
  if (val === null || val === undefined || val === "") return true;
  // `!v` is not the same as "blank": a blank whose answer is the number 0 is falsy, and testing it
  // that way marked a correctly answered fill empty.
  if (Array.isArray(val)) return val.every((v) => String(v ?? "").trim() === "");
  if (typeof val === "object") {
    // Interactive written-companion value { interactive, text } — the inner value is an array of
    // placements for a plain plotting part but { points, edges } for an edge-drawing one.
    if (val.interactive != null && interactiveHasContent(val.interactive)) return false;
    // A drawing answer is an object in its own right ({ points, edges }, { lines, none },
    // { cut, offsets }), not only an { interactive, text } wrapper — and a declared "no lines of
    // symmetry" carries no array at all, so it read as an untouched canvas.
    if (interactiveHasContent(val)) return false;
    // Combined fill+drawing: typed boxes live in `blanks` next to the canvas value.
    if (Array.isArray(val.blanks) && val.blanks.some((v) => String(v ?? "").trim() !== "")) return false;
    if (val.blanks && typeof val.blanks === "object" && !Array.isArray(val.blanks)
      && Object.values(val.blanks).some((v) => String(v ?? "").trim() !== "")) return false;
    // Matching value: a { leftItem: rightItem } map with no text/drawing key of its own.
    if (Object.values(val).some((v) => typeof v === "string" && v.trim() !== "")) return false;
    return String(val.text ?? "").trim() === "" && !val.drawing;
  }
  return String(val).trim() === "";
};

export const answerValueHasContent = (val) => !isAnswerValueEmpty(val);
